// components/DeleteOrganisationModal.tsx
import React, { useState, useCallback, FormEvent, ChangeEvent } from 'react';
import { Loader2 } from '@/components/ui/lucide-memo';
import { toast } from 'sonner';
import { useKernelStore } from '@/kernel/kernelStores';

interface DeletionOptions {
  deleteUsers: boolean;
  deleteClerkUsers: boolean;
  deleteUserProjects: boolean;
  deletePlans: boolean;
  deleteGenerationLogs: boolean;
}

interface DeleteOrganisationModalProps {
  isOpen: boolean;
  onClose: () => void;
  organisationId: string;
  // onSubmit should handle the API call and potential errors
  onSubmit: (orgId: string, options: DeletionOptions) => Promise<void>;
}

const OPTIONS: Array<{ key: keyof DeletionOptions; label: string; hint: string }> = [
  { key: 'deleteUsers', label: 'Its users', hint: 'Their accounts in the database.' },
  { key: 'deleteClerkUsers', label: 'Their sign-in accounts', hint: 'Removes them from Clerk too. Otherwise the accounts are left orphaned.' },
  { key: 'deleteUserProjects', label: 'Their projects', hint: 'Needs the users ticked. Files in storage stay.' },
  { key: 'deletePlans', label: 'Its plans', hint: 'The plans and subscriptions on record.' },
  { key: 'deleteGenerationLogs', label: 'Its generation history', hint: 'Every generation the organisation ever ran.' },
];

const DeleteOrganisationModal: React.FC<DeleteOrganisationModalProps> = ({
  isOpen,
  onClose,
  organisationId,
  onSubmit,
}) => {
  const organisationName = useKernelStore((state) => state.inventory.Organisation.registry[organisationId]?.name ?? '');
  const [options, setOptions] = useState<DeletionOptions>({
    deleteUsers: false,
    deleteClerkUsers: false,
    deleteUserProjects: false,
    deletePlans: false,
    deleteGenerationLogs: false,
  });
  const [confirmationInput, setConfirmationInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isConfirmationValid = organisationName !== '' && confirmationInput === organisationName;

  const handleCheckboxChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setOptions(prev => {
        const newOptions = { ...prev, [name]: checked };
        // Automatically check dependent options or warn
        if (name === 'deleteUserProjects' && checked && !newOptions.deleteUsers) {
             toast.warning("Deleting projects requires deleting users. 'Its users' will be ticked automatically.", { duration: 5000 });
             newOptions.deleteUsers = true; // Auto-check
        }
        if (name === 'deleteUsers' && checked && !newOptions.deleteClerkUsers) {
            toast.warning("Deleting DB users without deleting their sign-in accounts will orphan accounts. Consider ticking 'Their sign-in accounts'.", { duration: 5000 });
        }
         // Uncheck dependencies if parent is unchecked
         if (name === 'deleteUsers' && !checked) {
             newOptions.deleteUserProjects = false;
         }
        return newOptions;
    });
  }, []);

  const handleConfirmationInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setConfirmationInput(e.target.value);
  }, []);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!isConfirmationValid) {
      setError('Confirmation text does not match organisation name.');
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await onSubmit(organisationId, options);
      toast.success(`Organisation "${organisationName}" submitted for deletion.`);
      // onClose(); // Let the parent handle closing on success if needed after state update
    } catch (submitError: any) {
      setError(submitError.message || 'Failed to submit deletion request.');
      toast.error(`Deletion failed: ${submitError.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [isConfirmationValid, onSubmit, organisationId, options, organisationName]);

  // The caller mounts the dialog per organisation, so its state starts fresh each time.
  if (!isOpen) return null;

  // ChatGPT's confirmation sheet: a #303030 dialog, one question as the title, plain rows to tick,
  // the name to retype, then a quiet Cancel and a soft-red Delete. No warning icons, no coloured
  // boxes (owner, 2026-09-29).
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={isSubmitting ? undefined : onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="delete-org-title" onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[480px] rounded-[24px] bg-[#303030] p-6 text-[#ECECEC] shadow-2xl">
        <h2 id="delete-org-title" className="font-semibold" style={{ fontSize: 18, lineHeight: '24px' }}>Delete {organisationName}?</h2>
        <p className="mt-2 text-[#AFAFAF]" style={{ fontSize: 14, lineHeight: '20px' }}>
          The organisation is removed for good, along with whatever you tick below. This cannot be undone.
        </p>

        <form onSubmit={handleSubmit} className="mt-5">
          <div className="flex flex-col">
            {OPTIONS.map(({ key, label, hint }) => {
              const blocked = key === 'deleteUserProjects' && !options.deleteUsers;
              return (
                <label key={key} htmlFor={`delete-${key}`} className={`flex cursor-pointer items-start gap-3 rounded-[12px] px-2 py-2.5 transition-colors hover:bg-white/[0.04] ${blocked ? 'opacity-50' : ''}`}>
                  <input
                    type="checkbox"
                    id={`delete-${key}`}
                    name={key}
                    checked={options[key]}
                    onChange={handleCheckboxChange}
                    className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded accent-[#ECECEC]"
                    disabled={isSubmitting || blocked}
                  />
                  <span className="min-w-0">
                    <span className="block" style={{ fontSize: 14, lineHeight: '20px' }}>{label}</span>
                    <span className="block text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '18px' }}>{hint}</span>
                  </span>
                </label>
              );
            })}
          </div>

          <label htmlFor="org-name-confirm" className="mt-5 block text-[#AFAFAF]" style={{ fontSize: 13 }}>
            Type <span className="text-[#ECECEC]" style={{ fontSize: 'inherit' }}>{organisationName}</span> to confirm
          </label>
          <input
            type="text"
            id="org-name-confirm"
            value={confirmationInput}
            onChange={handleConfirmationInputChange}
            required
            autoFocus
            className="mt-2 h-10 w-full rounded-full border border-[#444444] bg-transparent px-4 text-[#ECECEC] outline-none transition-colors placeholder:text-[#8E8E8E] focus:border-[#8E8E8E]"
            style={{ fontSize: 14 }}
            placeholder={organisationName}
            disabled={isSubmitting}
            autoComplete="off"
          />
          {error && <p className="mt-2 text-[#F28B82]" style={{ fontSize: 13 }}>{error}</p>}

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="h-9 rounded-full border border-[#444444] px-4 text-[#ECECEC] transition-colors hover:bg-white/[0.06] disabled:opacity-40"
              style={{ fontSize: 14 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isConfirmationValid || isSubmitting}
              className="inline-flex h-9 min-w-[88px] items-center justify-center rounded-full border border-[#F28B82]/40 px-4 font-medium text-[#F28B82] transition-colors hover:bg-[#F28B82]/10 disabled:cursor-not-allowed disabled:opacity-40"
              style={{ fontSize: 14 }}
            >
              {isSubmitting ? <Loader2 size={15} className="animate-spin" /> : 'Delete'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeleteOrganisationModal;
